import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { OrderStatusMessageCardBlockConfig } from '@front-platform/content-builder';
import { orderStatusMessageCardBlockResolveConfig } from '@front-platform/content-builder';
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, input } from '@angular/core';
import { skipWhile, takeUntil, tap } from 'rxjs/operators';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { OrderStoreSelector } from '@common/selectors/order.selector';
import { DoodOrderModel } from '@store/order/order.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface OrderStatusMessageCardBlockRuntimeConfig {}

export type OrderStatusMessageCardBlockConfigInput = Partial<
  Omit<OrderStatusMessageCardBlockConfig, keyof OrderStatusMessageCardBlockRuntimeConfig> &
    OrderStatusMessageCardBlockRuntimeConfig
>;
export type OrderStatusMessageCardBlockResolvedConfig = Omit<
  OrderStatusMessageCardBlockConfig,
  keyof OrderStatusMessageCardBlockRuntimeConfig
> &
  Partial<OrderStatusMessageCardBlockRuntimeConfig>;
export interface OrderStatusMessageCardBlockContract extends ConfigComponentContract<
  OrderStatusMessageCardBlockConfigInput,
  OrderStatusMessageCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-order-status-message-card-block',
  templateUrl: './order-status-message-card-block.component.html',
  standalone: false,
})
export class OrderStatusMessageCardBlockComponent
  extends DestroyerBase
  implements OnInit, OrderStatusMessageCardBlockContract
{
  readonly config = input<OrderStatusMessageCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    OrderStatusMessageCardBlockConfig,
    OrderStatusMessageCardBlockRuntimeConfig
  >(orderStatusMessageCardBlockResolveConfig, () => ({ config: this.config() }));
  get title(): string {
    return this.resolvedConfig()['title'] as unknown as string;
  }
  get content(): string {
    return this.resolvedConfig()['content'] as unknown as string;
  }
  get text(): string {
    return this.resolvedConfig()['text'] as unknown as string;
  }
  get displayOnlyIfTypes(): boolean {
    return this.resolvedConfig()['display_only_if_types'] as unknown as boolean;
  }
  get enabledForTypes(): string[] {
    return this.resolvedConfig()['enabled_for_types'] as unknown as string[];
  }
  get displayOnlyIfPaymentMethods(): boolean {
    return this.resolvedConfig()['display_only_if_payment_methods'] as unknown as boolean;
  }
  get displayIfOrderAmountEqualZero(): boolean {
    return this.resolvedConfig()['display_if_order_amount_equal_zero'] as unknown as boolean;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get enabledForPaymentMethods(): string[] {
    return this.resolvedConfig()['enabled_for_payment_methods'] as unknown as string[];
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  displayCard = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private orderSelector: OrderStoreSelector,
  ) {
    super();
  }

  ngOnInit(): void {
    const orderId = this.activatedRoute.snapshot.params.orderId;
    this.orderSelector
      .selectOrder(orderId)
      .pipe(
        takeUntil(this._destroyerRef),
        skipWhile(order => !order),
        tap(order => this.checkDisplayConditions(order)),
      )
      .subscribe();
  }

  private checkDisplayConditions(order?: DoodOrderModel): void {
    this.displayCard = false;
    if (!order) {
      return;
    }

    if (
      this.displayOnlyIfPaymentMethods &&
      !this.enabledForPaymentMethods.includes(order.payment_service)
    ) {
      return;
    }
    if (this.displayOnlyIfTypes && !this.enabledForTypes.includes(order.type)) {
      return;
    }

    if (!this.displayIfOrderAmountEqualZero && order.final_price === 0) {
      return;
    }

    this.displayCard = true;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(OrderStatusMessageCardBlockComponent, {
  'keyAliases': {},
});
